import React, { useState, useEffect, useCallback, useRef } from "react";
import { History, Activity as ActivityIcon, AlertTriangle, Download, Loader } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { parseUtcDate, formatUtcDateTime } from "../utils/dateTime";

/**
 * The asset's Activity tab: everything Axaris holds about one AI asset, from five stores, in one
 * newest-first list. The server merges, orders and pages (`GET /ai-assets/{id}/history`); this
 * file only renders what it is given, says which sources it could not read, and exports.
 *
 * Language rule: the tab's own words never claim the list is final or tamper-proof. Entry values
 * (kind, summary, detail, actor) are rendered exactly as the server sent them.
 */

export const HISTORY_PAGE_SIZE = 100;

/** Per-entry provenance chip. Where a line came from is the point of the view. */
export const SOURCE_LABEL = {
  Activity: "Axaris action",
  Behaviour: "Observed change",
  Resolution: "Decision",
  EstateAlert: "Estate alert",
  ResponseLog: "Response log",
};

/** The same sources named as collections, for the coverage sentence. */
const SOURCE_COLLECTION = {
  Activity: "Axaris actions",
  Behaviour: "observed changes",
  Resolution: "decisions",
  EstateAlert: "estate alerts",
  ResponseLog: "response log entries",
};

const collectionName = (source) => SOURCE_COLLECTION[source] || source;
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const SOURCE_CHIP = {
  Activity: "bg-slate-50 text-slate-700 border-slate-200",
  Behaviour: "bg-amber-50 text-amber-800 border-amber-200",
  Resolution: "bg-blue-50 text-blue-700 border-blue-200",
  EstateAlert: "bg-purple-50 text-purple-700 border-purple-200",
  ResponseLog: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const entryKey = (e) => `${e.source}:${e.sourceRowId}`;

/** Appends a page, dropping entries already shown — display only; paging follows the raw offset. */
function appendUnique(existing, page) {
  const seen = new Set(existing.map(entryKey));
  const out = [...existing];
  for (const e of page) {
    const k = entryKey(e);
    if (!seen.has(k)) { seen.add(k); out.push(e); }
  }
  return out;
}

const coverageSignature = (coverage) =>
  (coverage ?? []).map((c) => `${c.source}:${c.read ? 1 : 0}`).join("|");

const INITIAL = {
  status: "loading", // loading | ready | notAvailable | error
  entries: [],
  offset: 0, // raw server offset: sum of the page sizes the server returned
  total: 0,
  behaviourFloor: null,
  coverage: [],
  exhausted: false,
  loadingMore: false,
  loadMoreFailed: false,
};

/**
 * Reads the merged history for one asset. `refreshToken` changing refetches page one in place
 * (same asset — content stays mounted); `assetId` changing resets outright.
 */
export function useAssetHistory(assetId, refreshToken) {
  const apiCall = useApi();
  const [state, setState] = useState(INITIAL);
  // Every request carries the generation it was issued under; a response from an older one is
  // dropped, so a slow page for the previous asset can never land on this one.
  const generation = useRef(0);
  const shownFor = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const fetchPage = useCallback((skip) =>
    // `since` is deliberately never sent: the default is the asset's whole history.
    apiCall("GET", `/ai-assets/${assetId}/history?skip=${skip}&take=${HISTORY_PAGE_SIZE}`),
  [apiCall, assetId]);

  const loadFirst = useCallback(() => {
    const gen = ++generation.current;
    if (shownFor.current !== assetId) {
      shownFor.current = assetId;
      setState(INITIAL);
    } else {
      setState((s) => (s.status === "ready" ? s : { ...INITIAL }));
    }
    fetchPage(0)
      .then((page) => {
        if (gen !== generation.current || !page) return; // stale, or redirected to sign-in
        const rows = page.entries ?? [];
        setState({
          ...INITIAL,
          status: "ready",
          entries: appendUnique([], rows),
          offset: rows.length,
          total: page.total ?? 0,
          behaviourFloor: page.behaviourFloor ?? null,
          coverage: page.coverage ?? [],
          exhausted: rows.length === 0,
        });
      })
      .catch((err) => {
        if (gen !== generation.current) return;
        // 404 is the endpoint's answer for "outside your scope" as much as "no such asset" —
        // not a failure, and not worth a retry.
        const status = err?.response?.status === 404 ? "notAvailable" : "error";
        setState({ ...INITIAL, status });
      });
  }, [fetchPage, assetId]);

  useEffect(() => {
    loadFirst();
    return () => { generation.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, refreshToken]);

  const loadMore = useCallback(() => {
    const current = stateRef.current;
    if (current.status !== "ready" || current.loadingMore) return;
    const gen = generation.current;
    setState((s) => ({ ...s, loadingMore: true, loadMoreFailed: false }));
    fetchPage(current.offset)
      .then((page) => {
        if (gen !== generation.current || !page) return;
        const rows = page.entries ?? [];
        // The history moved under us (a new entry, a source that failed or recovered): the
        // offset no longer means what it did, so start again from the top.
        if ((page.total ?? 0) !== current.total
            || coverageSignature(page.coverage) !== coverageSignature(current.coverage)) {
          loadFirst();
          return;
        }
        setState((s) => ({
          ...s,
          loadingMore: false,
          entries: appendUnique(s.entries, rows),
          offset: s.offset + rows.length,
          exhausted: rows.length === 0,
        }));
      })
      .catch(() => {
        if (gen !== generation.current) return;
        setState((s) => ({ ...s, loadingMore: false, loadMoreFailed: true }));
      });
  }, [fetchPage, loadFirst]);

  return { ...state, loadMore, retry: loadFirst };
}

/** True when every source reported was read. */
const allSourcesRead = (coverage) => (coverage ?? []).length > 0 && coverage.every((c) => c.read);

/**
 * The tab label's count. Empty while loading, when the history is not available, on error — and
 * whenever any source could not be read: a number that silently leaves out a failed store is
 * worse than no number.
 */
export function historyTabCount(history) {
  if (!history || history.status !== "ready") return "";
  if (!allSourcesRead(history.coverage)) return "";
  return history.total > 0 ? ` (${history.total})` : "";
}

const pad = (n) => String(n).padStart(2, "0");
const utcStamp = (d) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`;

function CoverageLine({ coverage }) {
  if (!coverage?.length) return null;
  const read = coverage.filter((c) => c.read);
  const unread = coverage.filter((c) => !c.read);
  const names = (list) => list.map((c) => collectionName(c.source)).join(", ");
  return (
    <div className="text-xs space-y-0.5" data-testid="history-coverage">
      {unread.length === 0 ? (
        <div className="text-content-secondary">
          Read from all {coverage.length} sources: {names(read)}.
        </div>
      ) : (
        <>
          {read.length > 0 && (
            <div className="text-content-secondary">
              Read: {names(read)}.
            </div>
          )}
          {unread.map((c) => (
            <div key={c.source} className="text-amber-700" title={c.unavailableReason || undefined}>
              <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
              {capitalize(collectionName(c.source))} could not be read, so this timeline is incomplete.
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function WindowLine({ floor }) {
  const d = parseUtcDate(floor);
  if (!d) return null;
  const days = Math.max(0, Math.round((Date.now() - d.getTime()) / 86400000));
  return (
    <div className="text-xs text-content-secondary" data-testid="history-window">
      Behavioural events are kept for {days} days; older activity is still shown.
    </div>
  );
}

function ExportControls({ assetId, disabled, partial }) {
  const apiCall = useApi();
  const [exporting, setExporting] = useState(false);
  const [failed, setFailed] = useState(false);

  const download = async (format) => {
    setExporting(true);
    setFailed(false);
    try {
      const blob = await apiCall(
        "GET", `/ai-assets/${assetId}/history/export?format=${format}`, null, false, "blob");
      if (!blob) return; // redirected to sign-in
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-asset-${assetId}-history-${utcStamp(new Date())}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setFailed(true);
    } finally {
      setExporting(false);
    }
  };

  // A partial export is legitimate — the file states its own coverage — but the button must not
  // read as though it holds everything.
  const label = (f) => (partial ? `Export available sources (${f})` : `Export ${f}`);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {["json", "csv"].map((f) => (
        <button
          key={f}
          type="button"
          onClick={() => download(f)}
          disabled={disabled || exporting}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border text-content-secondary hover:bg-theme-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-3 h-3" />
          {label(f.toUpperCase())}
        </button>
      ))}
      {failed && <span className="text-xs text-red-600">Export failed.</span>}
    </div>
  );
}

function HistoryEntry({ e }) {
  const observed = e.source === "Behaviour";
  return (
    <div className="flex items-start gap-2 text-sm" data-testid="history-entry">
      {observed
        ? <ActivityIcon className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
        : <History className="w-3.5 h-3.5 text-content-secondary shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <span className={`inline-block mr-1.5 px-1.5 py-px rounded border text-[11px] align-middle ${
          SOURCE_CHIP[e.source] || "bg-slate-50 text-slate-700 border-slate-200"
        }`}>
          {SOURCE_LABEL[e.source] || e.source}
        </span>
        <span className="text-content-primary">{e.summary}</span>
        <span className="text-content-secondary text-xs">
          {" — "}{e.kind}
          {e.actorUserName && <>{" · "}{e.actorUserName}</>}
          {" · "}{formatUtcDateTime(e.occurredAt)}
        </span>
        {e.detail && (
          <div className="text-xs text-content-secondary mt-0.5 break-words">{e.detail}</div>
        )}
        {e.riskDeleted && (
          <div className="text-xs text-amber-700 mt-0.5">
            The risk this entry refers to has since been deleted.
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Renders a `useAssetHistory` result. Kept separate from the hook so the tab label can show the
 * count from the same read without a second request.
 */
export default function AssetActivityTrail({ history, assetId, startExpanded = false }) {
  const [expanded, setExpanded] = useState(startExpanded);
  const h = history ?? INITIAL;

  if (h.status === "notAvailable") {
    return (
      <div className="text-xs text-content-secondary">
        This asset&apos;s history is not available for your scope.
      </div>
    );
  }

  if (h.status === "error") {
    return (
      <div className="text-xs text-content-secondary flex items-center gap-2">
        History could not be loaded.
        {h.retry && (
          <button type="button" onClick={h.retry} className="text-indigo-600 hover:text-indigo-800">
            Retry
          </button>
        )}
      </div>
    );
  }

  const loading = h.status === "loading";
  const entries = h.entries ?? [];
  const partial = !loading && !allSourcesRead(h.coverage);
  const shown = expanded ? entries : entries.slice(0, 3);
  const hasMore = !loading && !h.exhausted && h.offset < h.total;

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="space-y-0.5">
          {!loading && <CoverageLine coverage={h.coverage} />}
          {!loading && h.behaviourFloor && <WindowLine floor={h.behaviourFloor} />}
        </div>
        <ExportControls assetId={assetId} disabled={loading} partial={partial} />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-content-secondary">
          <Loader className="w-3.5 h-3.5 animate-spin" /> Loading history…
        </div>
      ) : entries.length === 0 ? (
        <div className="text-xs text-content-secondary">
          {partial
            ? "No entries from the sources that could be read."
            : "No activity recorded for this asset yet."}
        </div>
      ) : (
        <>
          <div className="space-y-1.5">
            {shown.map((e) => <HistoryEntry key={entryKey(e)} e={e} />)}
          </div>
          {!expanded && entries.length > 3 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-xs text-indigo-600 hover:text-indigo-800"
            >
              Show all {entries.length}
            </button>
          )}
          {expanded && (
            <div className="flex items-center gap-3 text-xs text-content-secondary">
              <span>Showing {entries.length} of {h.total}</span>
              {hasMore && (
                <button
                  type="button"
                  onClick={h.loadMore}
                  disabled={h.loadingMore}
                  className="text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                >
                  {h.loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
              {h.loadMoreFailed && <span className="text-red-600">The next page could not be loaded.</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
